import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../auth/AuthProvider";
import PageHeader from "../../components/PageHeader";
import Toggle from "../../components/Toggle";
import { Banner, useToast } from "../../components/Feedback";

/**
 * Bot rakip ayarları.
 *
 * Gerçek rakip bulunamadığında odaya giren bot buradan yönetiliyor. Belge
 * (`config/bot`) uygulama tarafından CANLI dinleniyor: kaydedilen değer,
 * kimsenin uygulamayı güncellemesi gerekmeden saniyeler içinde geçerli olur.
 * `Bot rakip` anahtarı bu yüzden aynı zamanda bir acil durum şalteri.
 *
 * Beynin bütün sabitleri bilerek buraya açılmadı. Panelde yirmi ham sayı,
 * dengeyi bir gecede bozup nedenini kimsenin bilmediği bir duruma yol açar.
 * Açılanlar işletmenin gerçekten ihtiyaç duyduğu kararlar: açık mı, ne kadar
 * beklenir, ne kadar zor. Geri kalanı kodda ve simülasyon testiyle korunuyor.
 */

/// 1 saniye test için: "hemen gelsin" diyebilmek, davranışı doğrulamanın en
/// hızlı yolu. Alt sınır 3'ken 2 yazan kişi Kaydet düğmesinin kapandığını
/// görüp "kaydetmiyor" sanıyordu.
const MIN_WAIT = 1;
const MAX_WAIT = 180;

type Difficulty = "kolay" | "dengeli" | "zorlu";

type BotConfigDoc = {
  enabled: boolean;
  summonMinSeconds: number;
  summonMaxSeconds: number;
  newPlayerSummonMinSeconds: number;
  newPlayerSummonMaxSeconds: number;
  botStartsChance: number;
  difficulty: Difficulty;
};

/** Uygulamadaki `BotSettingsData.defaults` ile aynı olmalı. */
const DEFAULTS: BotConfigDoc = {
  enabled: true,
  summonMinSeconds: 13,
  summonMaxSeconds: 27,
  newPlayerSummonMinSeconds: 7,
  newPlayerSummonMaxSeconds: 14,
  botStartsChance: 0.45,
  difficulty: "dengeli",
};

const DIFFICULTY_INFO: Record<
  Difficulty,
  { title: string; blurb: string; winRate: string }
> = {
  kolay: {
    title: "Kolay",
    blurb: "Bot turları daha çok bırakır. Yeni oyuncuyu tutmak için.",
    winRate: "oyuncu ~%75-90 kazanır",
  },
  dengeli: {
    title: "Dengeli",
    blurb: "Ölçülmüş varsayılan. Maçlar başa baş gider.",
    winRate: "oyuncu ~%62-84 kazanır",
  },
  zorlu: {
    title: "Zorlu",
    blurb: "Bot inatçı. Galibiyet daha anlamlı, yenilgi de sıklaşır.",
    winRate: "oyuncu ~%50-75 kazanır",
  },
};

export default function BotSettings() {
  const { user } = useAuth();
  const [saved, setSaved] = useState<BotConfigDoc | null>(null);
  const [draft, setDraft] = useState<BotConfigDoc>(DEFAULTS);
  const [meta, setMeta] = useState<{ by?: string; atMs?: number }>({});
  const [loading, setLoading] = useState(true);
  const [docMissing, setDocMissing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { show, node: toast } = useToast();

  // Kullanıcı forma dokundu mu?
  //
  // Anlık görüntü geldiğinde taslağın tazelenip tazelenmeyeceğini bu belirler.
  // Eskiden bunun yerine "taslak kayıttan farklı mı" karşılaştırması vardı ve
  // yanılabiliyordu: form belge yüklenmeden varsayılanlarla doluyor,
  // dolayısıyla "farklı" görünen bir taslak aslında hiç dokunulmamış olabiliyordu.
  const editedRef = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config", "bot"),
      (snap) => {
        const x = snap.data();
        const next: BotConfigDoc = {
          enabled: (x?.enabled as boolean) ?? DEFAULTS.enabled,
          summonMinSeconds:
            (x?.summonMinSeconds as number) ?? DEFAULTS.summonMinSeconds,
          summonMaxSeconds:
            (x?.summonMaxSeconds as number) ?? DEFAULTS.summonMaxSeconds,
          newPlayerSummonMinSeconds:
            (x?.newPlayerSummonMinSeconds as number) ??
            DEFAULTS.newPlayerSummonMinSeconds,
          newPlayerSummonMaxSeconds:
            (x?.newPlayerSummonMaxSeconds as number) ??
            DEFAULTS.newPlayerSummonMaxSeconds,
          botStartsChance:
            (x?.botStartsChance as number) ?? DEFAULTS.botStartsChance,
          difficulty: (x?.difficulty as Difficulty) ?? DEFAULTS.difficulty,
        };
        setSaved(next);
        setDocMissing(!snap.exists());
        setMeta({
          by: x?.updatedByEmail as string | undefined,
          atMs: x?.updatedAt?.toMillis?.(),
        });
        // Kullanıcı forma dokunmadıysa taslağı tazele. Dokunduysa
        // dokunmuyoruz — başka bir sekmeden gelen güncelleme, yazılmakta olan
        // değeri altından çekmemeli.
        if (!editedRef.current) setDraft(next);
        setLoading(false);
        setErr(null);
      },
      (e) => {
        // Belge okunamadı. Formu AÇMIYORUZ: varsayılanlarla dolu bir taslağı
        // kaydetmek, sunucudaki gerçek ayarların üstüne varsayılan yazmak
        // demek olurdu.
        setErr(
          `Ayarlar okunamadı: ${e.message}. Sayfayı yenile — ayarları bu ` +
            `hâlde kaydetmek mevcut değerlerin üstüne yazabilir.`
        );
      }
    );
    return unsub;
  }, []);

  const dirty = saved !== null && dirtyAgainst(draft, saved);

  const problems = useMemo(() => validate(draft), [draft]);

  const set = <K extends keyof BotConfigDoc>(key: K, value: BotConfigDoc[K]) => {
    editedRef.current = true;
    setDraft((d) => ({ ...d, [key]: value }));
  };

  /**
   * YALNIZCA DEĞİŞEN ALANLARI yazar.
   *
   * Eskiden taslağın tamamı gönderiliyordu ve bu sessizce zarar veriyordu:
   * form belge yüklenmeden varsayılanlarla doluyor, dolayısıyla tek bir
   * alanı değiştirip kaydeden kişi dokunmadığı alanların üstüne varsayılan
   * yazıyordu. Panelde "kaydediyorum ama değerim geri geliyor" diye görünen
   * şey buydu.
   */
  const save = async () => {
    if (problems.length > 0 || saved === null) return;
    setBusy(true);
    setErr(null);
    try {
      const changed: Partial<BotConfigDoc> = {};
      for (const key of Object.keys(DEFAULTS) as (keyof BotConfigDoc)[]) {
        if (draft[key] !== saved[key]) {
          (changed as Record<string, unknown>)[key] = draft[key];
        }
      }
      if (Object.keys(changed).length === 0) {
        setBusy(false);
        return;
      }
      await setDoc(
        doc(db, "config", "bot"),
        {
          ...changed,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid ?? null,
          updatedByEmail: user?.email ?? null,
        },
        { merge: true }
      );
      editedRef.current = false;
      show("Ayarlar kaydedildi. Uygulamalarda saniyeler içinde geçerli olur.");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Belge yüklenmeden form açılmıyor. Varsayılanlarla dolu bir taslağı
  // kaydetmek, sunucudaki gerçek ayarların üstüne yazmak demek olurdu.
  if (loading) {
    return (
      <div className="pb-24">
        <PageHeader title="Bot Rakip" />
        {err ? (
          <Banner tone="error">{err}</Banner>
        ) : (
          <div className="card p-8 text-sm text-textMuted">Yükleniyor…</div>
        )}
      </div>
    );
  }

  return (
    <div className="pb-24">
      <PageHeader
        title="Bot Rakip"
        subtitle="Gerçek rakip bulunamadığında odaya giren rakip. Buradaki değişiklikler uygulamalara canlı iner — sürüm çıkmaya gerek yok."
        actions={
          <>
            <button
              className="btn-ghost text-sm"
              disabled={!dirty || busy}
              onClick={() => {
                if (!saved) return;
                editedRef.current = false;
                setDraft(saved);
              }}
            >
              Geri al
            </button>
            <button
              className="btn-primary text-sm"
              disabled={!dirty || busy || problems.length > 0}
              onClick={save}
            >
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </>
        }
      />

      {err && (
        <Banner tone="error" onClose={() => setErr(null)}>
          {err}
        </Banner>
      )}

      {docMissing && (
        <Banner tone="info">
          Ayar belgesi henüz yok; uygulamalar koddaki varsayılanlarla
          çalışıyor. İlk kaydetmede belge oluşturulur.
        </Banner>
      )}

      {problems.length > 0 && (
        <Banner tone="warn">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Banner>
      )}

      {dirty && problems.length === 0 && (
        <Banner tone="warn">
          Kaydedilmemiş değişiklik var.
        </Banner>
      )}

      {/* --- Şalter ------------------------------------------------- */}
      <section className="card p-5 mb-4">
        <div className="flex items-center gap-4">
          <div
            className={`h-12 w-12 rounded-2xl grid place-items-center text-2xl shrink-0 ${
              draft.enabled ? "bg-success/15" : "bg-danger/15"
            }`}
          >
            🤖
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-extrabold text-lg">Bot rakip</h2>
              <span
                className={`pill border ${
                  draft.enabled
                    ? "bg-success/20 text-success border-success/40"
                    : "bg-danger/20 text-danger border-danger/40"
                }`}
              >
                {draft.enabled ? "AÇIK" : "KAPALI"}
              </span>
            </div>
            <p className="text-sm text-textSecondary mt-1">
              Kapatınca <b>yeni</b> bot maçı açılmaz. O sırada oynanan maçlar
              sonuna kadar devam eder — süren bir maçı kesmek, oyuncuyu donmuş
              bir rakiple baş başa bırakırdı.
            </p>
          </div>
          <Toggle
            checked={draft.enabled}
            onChange={(v) => set("enabled", v)}
            title={draft.enabled ? "Kapat" : "Aç"}
          />
        </div>
      </section>

      {/* --- Bekleme süreleri --------------------------------------- */}
      <section className="card p-5 mb-4">
        <h2 className="font-extrabold text-lg">Ne kadar sonra girsin</h2>
        <p className="text-sm text-textSecondary mt-1 mb-5">
          Oyuncu bu aralıkta rastgele bir süre gerçek rakip bekler, sonra bot
          girer. Aralık olmasının sebebi: her maçta aynı saniyede birinin
          gelmesi tek başına botu ele verir. Gerçek bir oyuncu bu süre dolmadan
          girerse bot hiç çağrılmaz.
        </p>

        <RangeRow
          label="Deneyimli oyuncu"
          hint="En az 4 maç oynamış hesaplar"
          min={draft.summonMinSeconds}
          max={draft.summonMaxSeconds}
          onMin={(v) => set("summonMinSeconds", v)}
          onMax={(v) => set("summonMaxSeconds", v)}
        />

        <div className="h-px bg-stroke/70 my-5" />

        <RangeRow
          label="Yeni oyuncu"
          hint="İlk 3 maçı. Boş lobiyle karşılaşan yeni oyuncu geri gelmiyor — bu yüzden daha kısa."
          min={draft.newPlayerSummonMinSeconds}
          max={draft.newPlayerSummonMaxSeconds}
          onMin={(v) => set("newPlayerSummonMinSeconds", v)}
          onMax={(v) => set("newPlayerSummonMaxSeconds", v)}
        />
      </section>

      {/* --- Zorluk ------------------------------------------------- */}
      <section className="card p-5 mb-4">
        <h2 className="font-extrabold text-lg">Zorluk</h2>
        <p className="text-sm text-textSecondary mt-1 mb-5">
          Bot ayrıca oyuncunun geçmişine göre kendini ayarlıyor (yeni oyuncuya
          nazik, kazanma oranı yüksek oyuncuya sert) ve maç içinde skora göre
          gevşeyip sıkılaşıyor. Buradaki kadran bunun üstüne binen genel yön.
          Süren maçların zorluğu değişmez; kadran maç başlarken sabitlenir.
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          {(Object.keys(DIFFICULTY_INFO) as Difficulty[]).map((key) => {
            const info = DIFFICULTY_INFO[key];
            const active = draft.difficulty === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => set("difficulty", key)}
                className={`text-left rounded-2xl border p-4 transition ${
                  active
                    ? "border-primary bg-primary/15"
                    : "border-stroke bg-surfaceHigh/40 hover:border-strokeStrong"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-extrabold">{info.title}</span>
                  {active && (
                    <span className="pill bg-primary/25 text-primarySoft text-[10px]">
                      SEÇİLİ
                    </span>
                  )}
                </div>
                <p className="text-xs text-textSecondary mt-2 leading-relaxed">
                  {info.blurb}
                </p>
                <p className="text-[11px] text-textMuted mt-2">
                  {info.winRate}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {/* --- İnce ayar ---------------------------------------------- */}
      <section className="card p-5 mb-4">
        <h2 className="font-extrabold text-lg">İnce ayar</h2>

        <div className="mt-5 flex items-center gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm">
              Maça bot başlama oranı
            </div>
            <p className="text-xs text-textSecondary mt-1">
              Oda kurucusu her maçta ilk oynasaydı, sıranın hiç değişmemesi
              başlı başına bir ipucu olurdu. %0 = maça hep oyuncu başlar.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(draft.botStartsChance * 100)}
              onChange={(e) =>
                set("botStartsChance", Number(e.target.value) / 100)
              }
              className="w-40 accent-primary"
            />
            <span className="font-extrabold tabular-nums w-12 text-right">
              %{Math.round(draft.botStartsChance * 100)}
            </span>
          </div>
        </div>
      </section>

      {/* --- Bilgi -------------------------------------------------- */}
      <section className="card p-5">
        <h2 className="font-extrabold text-lg mb-3">Nasıl çalışıyor</h2>
        <ul className="text-sm text-textSecondary space-y-2 leading-relaxed">
          <li>
            • Bot bir hesap değil. Odaya <code className="text-textPrimary">bot_</code>{" "}
            önekli misafir olarak yazılır; hamlelerini oyuncunun kendi cihazı
            sürer. Bu yüzden gecikme yok ve sunucu maliyeti sıfır.
          </li>
          <li>
            • Puanı yine yalnızca "süresi dolan tarafın rakibi" alır — güvenlik
            kuralı bot maçlarında da geçerli, kimse kendi skorunu yazamaz.
          </li>
          <li>
            • Bot maçları oyuncunun elmas, galibiyet ve serisine normal maçlar
            gibi işler. Oda kaydında <code className="text-textPrimary">botMatch</code>{" "}
            işareti durur; <b>Odalar</b> sayfasından ayırt edilebilir.
          </li>
          <li>
            • Uygulama sürümünde bot tamamen kapatılmışsa buradaki anahtar
            onu açamaz (derleme bayrağı <code className="text-textPrimary">BotConfig.enabled</code>).
          </li>
        </ul>

        <div className="mt-5 pt-4 border-t border-stroke/70 text-xs text-textMuted">
          Son değişiklik:{" "}
          {meta.atMs ? new Date(meta.atMs).toLocaleString("tr-TR") : "—"}
          {meta.by ? ` · ${meta.by}` : ""}
        </div>
      </section>

      {toast}
    </div>
  );
}

function RangeRow({
  label,
  hint,
  min,
  max,
  onMin,
  onMax,
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  onMin: (v: number) => void;
  onMax: (v: number) => void;
}) {
  const avg = Math.round((min + max) / 2);
  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm">{label}</div>
        <p className="text-xs text-textSecondary mt-1">{hint}</p>
        <p className="text-[11px] text-textMuted mt-2">
          Ortalama bekleme ≈ {avg} saniye
        </p>
      </div>
      <div className="flex items-end gap-2">
        <div>
          <label className="section-label block mb-1">En az</label>
          <input
            type="number"
            className="input w-24 py-2 text-center tabular-nums"
            min={MIN_WAIT}
            max={MAX_WAIT}
            value={min}
            onChange={(e) => onMin(Number(e.target.value))}
          />
        </div>
        <span className="pb-3 text-textMuted">–</span>
        <div>
          <label className="section-label block mb-1">En çok</label>
          <input
            type="number"
            className="input w-24 py-2 text-center tabular-nums"
            min={MIN_WAIT}
            max={MAX_WAIT}
            value={max}
            onChange={(e) => onMax(Number(e.target.value))}
          />
        </div>
        <span className="pb-3 text-sm text-textMuted">sn</span>
      </div>
    </div>
  );
}

/**
 * Kaydetmeden önce yakalanan hatalar.
 *
 * Uygulama tarafı bu değerleri ayrıca sınırlıyor — ama orada düzeltilen bir
 * değer, panelde yazdığından başka türlü çalışan bir bot demek. Hatayı burada
 * göstermek, sessizce düzeltmekten iyi.
 */
function validate(d: BotConfigDoc): string[] {
  const out: string[] = [];
  const range = (v: number, name: string) => {
    if (!Number.isFinite(v) || !Number.isInteger(v)) {
      out.push(`${name} tam sayı olmalı.`);
    } else if (v < MIN_WAIT || v > MAX_WAIT) {
      out.push(`${name} ${MIN_WAIT}-${MAX_WAIT} saniye arasında olmalı.`);
    }
  };
  range(d.summonMinSeconds, "Deneyimli oyuncu · en az");
  range(d.summonMaxSeconds, "Deneyimli oyuncu · en çok");
  range(d.newPlayerSummonMinSeconds, "Yeni oyuncu · en az");
  range(d.newPlayerSummonMaxSeconds, "Yeni oyuncu · en çok");

  if (d.summonMaxSeconds < d.summonMinSeconds) {
    out.push("Deneyimli oyuncuda en çok, en azdan küçük olamaz.");
  }
  if (d.newPlayerSummonMaxSeconds < d.newPlayerSummonMinSeconds) {
    out.push("Yeni oyuncuda en çok, en azdan küçük olamaz.");
  }
  return out;
}

function dirtyAgainst(a: BotConfigDoc, b: BotConfigDoc | null): boolean {
  if (!b) return false;
  return (
    a.enabled !== b.enabled ||
    a.summonMinSeconds !== b.summonMinSeconds ||
    a.summonMaxSeconds !== b.summonMaxSeconds ||
    a.newPlayerSummonMinSeconds !== b.newPlayerSummonMinSeconds ||
    a.newPlayerSummonMaxSeconds !== b.newPlayerSummonMaxSeconds ||
    a.botStartsChance !== b.botStartsChance ||
    a.difficulty !== b.difficulty
  );
}
